import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
} from "firebase/auth";
import { getAuthInstance } from "../firebase";

/**
 * Login con Apple usato da Login.jsx e Signup.jsx
 * - Web: Firebase Auth con signInWithPopup
 * - iOS/Android (app): plugin @capacitor-firebase/authentication con skipNativeAuth: true
 *   Il plugin mostra il popup Apple e restituisce i token.
 *   Noi facciamo signInWithCredential sul JS SDK per autenticare.
 *   Questo garantisce che Firestore veda request.auth correttamente!
 */
export async function signInWithApple() {
  const platform = Capacitor.getPlatform();
  console.log(`🍏 [AppleAuth] Avvio login Apple su piattaforma: ${platform}`);

  // 🌐 WEB - usa signInWithPopup direttamente
  if (!Capacitor.isNativePlatform()) {
    console.log("🌐 [AppleAuth] WEB → signInWithPopup");
    
    const auth = await getAuthInstance();
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    
    try {
      const result = await signInWithPopup(auth, provider);
      console.log("✅ [AppleAuth] WEB login OK:", result.user?.email);
      return { user: result.user };
    } catch (error) {
      console.error("❌ [AppleAuth] WEB error:", error.code, error.message);
      throw error;
    }
  }

  // 📱 NATIVE (iOS / Android)
  // Con skipNativeAuth: true, il plugin NON autentica su Firebase.
  // Restituisce solo i token (idToken + nonce) che usiamo per signInWithCredential.
  console.log("📱 [AppleAuth] NATIVE → skipNativeAuth:true - autenticazione manuale su JS SDK");

  try {
    // 1. Ottieni i token dal plugin (popup Apple nativo)
    console.log("🔄 [AppleAuth] NATIVE - Chiamata signInWithApple per ottenere token...");
    const nativeResult = await FirebaseAuthentication.signInWithApple({
      scopes: ["email", "name"],
    });

    console.log("📦 [AppleAuth] NATIVE result keys:", Object.keys(nativeResult));

    // 2. Verifica che abbiamo il credential con idToken
    if (!nativeResult.credential?.idToken) {
      console.error("❌ [AppleAuth] NATIVE - Nessun idToken nel credential!");
      console.error("❌ [AppleAuth] NATIVE - credential:", JSON.stringify(nativeResult.credential));
      throw new Error("Apple Sign-In completed but no idToken received");
    }

    console.log("🔑 [AppleAuth] NATIVE credential.idToken presente: true");
    console.log("🔑 [AppleAuth] NATIVE credential.nonce presente:", !!nativeResult.credential.nonce);

    // 3. Crea il credential OAuth per Firebase JS SDK
    const oauthProvider = new OAuthProvider("apple.com");
    const credential = oauthProvider.credential({
      idToken: nativeResult.credential.idToken,
      rawNonce: nativeResult.credential.nonce,
    });

    console.log("🔄 [AppleAuth] NATIVE - Credential OAuth creato, eseguo signInWithCredential...");

    // 4. Autentica su Firebase JS SDK
    const auth = await getAuthInstance();
    const userCredential = await signInWithCredential(auth, credential);

    console.log("✅ [AppleAuth] NATIVE login OK via JS SDK!");
    console.log("✅ [AppleAuth] NATIVE uid:", userCredential.user.uid);
    console.log("✅ [AppleAuth] NATIVE email:", userCredential.user.email);
    console.log("✅ [AppleAuth] NATIVE displayName:", userCredential.user.displayName);

    // 5. Restituisci l'utente autenticato (dal JS SDK, non dal plugin nativo!)
    return { 
      user: userCredential.user,
    };

  } catch (error) {
    console.error("❌ [AppleAuth] NATIVE error:", error);
    console.error("❌ [AppleAuth] NATIVE error message:", error.message);
    console.error("❌ [AppleAuth] NATIVE error code:", error.code);
    throw error;
  }
}
